// File: src/components/Payroll/SmartPayrollTable.jsx

import React, { useEffect, useState } from 'react';

const SmartPayrollTable = () => {
  const [data, setData] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPayrollData = async () => {
    try {
      const res = await fetch('/solo-mdsh-api/staffing/payroll-smart');
      if (!res.ok) throw new Error(`Server responded with ${res.status}`);
      const json = await res.json();
      setData(json.data || []);
    } catch (err) {
      console.error('❌ Failed to load payroll data:', err);
      setError('Could not load payroll data. Please check your backend connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayrollData();
  }, []);

  const filteredData = data.filter(entry => {
    const matchesStatus = statusFilter ? entry.status === statusFilter : true;
    const matchesSearch = searchTerm ? entry.full_name.toLowerCase().includes(searchTerm.toLowerCase()) : true;
    return matchesStatus && matchesSearch;
  });

  const handleExport = (format) => {
    const url = `/solo-mdsh-api/staffing/payroll-smart/export?format=${format}`;
    window.open(url, '_blank');
  };

  if (loading) return <div className="text-center py-10 text-white">⏳ Loading payroll report...</div>;
  if (error) return <div className="text-center text-red-500 py-10 font-semibold">{error}</div>;

  return (
    <div className="w-full mt-6 bg-glass p-6 rounded-xl shadow-lg">
      {/* Filters + Export */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border bg-white/10 text-white px-3 py-1 rounded shadow"
          >
            <option value="">All Statuses</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Overtime Approved">Overtime Approved</option>
          </select>

          <input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border bg-white/10 text-white px-3 py-1 rounded shadow"
          />

          <button
            onClick={() => { setSearchTerm(''); setStatusFilter(''); }}
            className="bg-white/10 hover:bg-white/20 text-white text-sm px-3 py-1 rounded shadow"
          >
            Reset
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => handleExport('csv')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded shadow"
          >
            Export CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded shadow"
          >
            Export PDF
          </button>
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div className="text-center text-gray-400 italic">No matching results found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full table-auto border border-gray-600 text-sm">
            <thead className="bg-gray-900 text-white font-semibold text-left">
              <tr>
                <th className="px-4 py-2">Staff</th>
                <th className="px-4 py-2">Event</th>
                <th className="px-4 py-2">Clock In</th>
                <th className="px-4 py-2">Clock Out</th>
                <th className="px-4 py-2">Hours</th>
                <th className="px-4 py-2">Pay Rate</th>
                <th className="px-4 py-2">Gross Pay</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Notes</th>
                <th className="px-4 py-2">Approver</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((entry) => (
                <tr key={entry.timesheet_id} className="hover:bg-white/10">
                  <td className="px-4 py-2 text-white font-medium">
                    {entry.full_name}
                    <div className="text-xs text-white/60">{entry.position}</div>
                  </td>
                  <td className="px-4 py-2 text-white/90">{entry.event_id}</td>
                  <td className="px-4 py-2 text-white/90">{entry.readable_clock_in}</td>
                  <td className="px-4 py-2 text-white/90">{entry.readable_clock_out}</td>
                  <td className="px-4 py-2 text-center text-white/90">{entry.total_hours}</td>
                  <td className="px-4 py-2 text-white/90">£{entry.pay_rate}</td>
                  <td className="px-4 py-2 text-white font-semibold">£{entry.gross_pay}</td>
                  <td className={`px-4 py-2 font-bold ${entry.status === 'Rejected' ? 'text-red-500' : 'text-green-400'}`}>
                    {entry.status}
                  </td>
                  <td className="px-4 py-2 text-white/80 max-w-xs truncate">{entry.notes}</td>
                  <td className="px-4 py-2 text-white/90">{entry.approved_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SmartPayrollTable;
